import { ReactNode } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Link,
  Tooltip,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DescriptionIcon from '@mui/icons-material/Description';
import { useLanguage } from '../../../../../context/LanguageContext';
import { useUser } from '../../../../../context/UserContext';
import { useBrandConfig } from '../../../../../context/BrandConfigContext';
import type { ValidationActions } from '../editor/useValidationActions';
import { RIGHTS, canSeeQuotation, full, useRights } from '../../../../../rights';

export interface ValidationAcknowledgements {
  bat: boolean;
  cgv: boolean;
  onBatChange: (checked: boolean) => void;
  onCgvChange: (checked: boolean) => void;
}

interface ValidationActionsCardProps {
  isWallet: boolean;
  // Faux pour un coupon caisse sans création : il n'y a pas de BAT à visualiser ni à valider
  showBat: boolean;
  hasCreation: boolean;
  isTestOperation: boolean;
  onTestOperationChange: (isTest: boolean) => void;
  acknowledgements: ValidationAcknowledgements;
  actions: ValidationActions;
  onOpenWalletPreview: () => void;
}

const GENERATING = 'GÉNÉRATION EN COURS…';

function AcknowledgeCheckbox({ checked, onChange, label, mt }: { checked: boolean; onChange: (checked: boolean) => void; label: ReactNode; mt?: number }) {
  return (
    <FormControlLabel
      control={<Checkbox checked={checked} onChange={e => onChange(e.target.checked)} sx={{ '&.Mui-checked': { color: 'primary.main' } }} />}
      label={<Typography variant="caption">{label}</Typography>}
      sx={{ display: 'flex', alignItems: 'center', mt }}
    />
  );
}

function ValidationActionsCard({
  isWallet, showBat, hasCreation, isTestOperation, onTestOperationChange, acknowledgements, actions, onOpenWalletPreview,
}: ValidationActionsCardProps) {
  const { t } = useLanguage();
  const { selectedWorkspace } = useUser();
  const { rights, can } = useRights();
  const { setting } = useBrandConfig();
  // Même règle qu'à la définition : réglage d'enseigne ET droit da.operation.create_test
  const showTestOperation = setting('module.test.operation.active') && can(full(RIGHTS.operation.createTest));
  const showQuotation = canSeeQuotation(rights, selectedWorkspace?.volumeName);
  const primaryBtn = { backgroundColor: 'primary.main', '&:hover': { backgroundColor: 'primary.main', opacity: 0.9 } };

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, backgroundColor: 'grey.50' }}>
      <CardContent>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
          {isWallet ? 'Visualiser le wallet' : showBat ? t('pages.marketing.communication.previewBat') : 'Devis de votre commande'}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          {isWallet
            ? 'Visualisez la notification et le verso de la carte avant de valider.'
            : showBat ? 'Visualisez le BAT de votre campagne avant de valider.' : "Ce coupon caisse n'a pas de création : il n'y a pas de BAT à visualiser."}
        </Typography>
        {!showBat ? null : isWallet ? (
          <Button variant="contained" fullWidth size="large" startIcon={<VisibilityIcon />} onClick={onOpenWalletPreview} sx={primaryBtn}>
            VISUALISER LE WALLET
          </Button>
        ) : (
          <Tooltip title={hasCreation ? '' : "Veuillez d'abord compléter l'étape Création"} disableHoverListener={hasCreation}>
            <span>
              <Button
                variant="contained"
                fullWidth
                size="large"
                startIcon={actions.previewingBat ? <CircularProgress size={18} color="inherit" /> : <VisibilityIcon />}
                disabled={!hasCreation || actions.previewingBat}
                onClick={actions.previewBat}
                sx={hasCreation ? primaryBtn : {}}
              >
                {actions.previewingBat ? GENERATING : 'PRÉVISUALISER LE BAT'}
              </Button>
            </span>
          </Tooltip>
        )}

        {showQuotation && (
          <Button
            variant="outlined"
            fullWidth
            size="large"
            startIcon={actions.downloadingDevis ? <CircularProgress size={18} color="inherit" /> : <DescriptionIcon />}
            disabled={actions.downloadingDevis}
            onClick={actions.downloadDevis}
            sx={{ mt: showBat ? 1.5 : 0, textTransform: 'none', fontWeight: 600, color: 'primary.main', borderColor: 'primary.main' }}
          >
            {actions.downloadingDevis ? GENERATING : 'VOIR LE DEVIS'}
          </Button>
        )}

        <Divider sx={{ my: 2 }} />

        {!isWallet && showBat && (
          <AcknowledgeCheckbox
            checked={acknowledgements.bat}
            onChange={acknowledgements.onBatChange}
            label="J'ai pris connaissance du BAT de ma campagne."
          />
        )}

        {showTestOperation && (
          <AcknowledgeCheckbox
            checked={isTestOperation}
            onChange={onTestOperationChange}
            label={t('pages.marketing.communication.testBeforeOperation')}
            mt={0.5}
          />
        )}

        <Alert severity="warning" sx={{ mt: 2, fontSize: '0.8rem' }}>
          Toute communication validée est définitive. Les conditions de modification et d&apos;annulation sont stipulées dans les{' '}
          <Link component="button" type="button" onClick={actions.downloadCgv} sx={{ fontWeight: 600 }}>
            conditions générales de vente
          </Link>.
        </Alert>

        <Button
          variant="text"
          size="small"
          startIcon={actions.downloadingCgv ? <CircularProgress size={14} color="inherit" /> : undefined}
          onClick={actions.downloadCgv}
          disabled={actions.downloadingCgv}
          sx={{ mt: 1, textTransform: 'none', fontWeight: 600, color: 'primary.main' }}
        >
          {actions.downloadingCgv ? GENERATING : 'Voir les conditions générales de vente'}
        </Button>

        <AcknowledgeCheckbox
          checked={acknowledgements.cgv}
          onChange={acknowledgements.onCgvChange}
          label="J'ai pris connaissance des conditions générales de vente et notamment de la limite de responsabilité de Kiss The Bride sur les délais."
          mt={0.5}
        />
      </CardContent>
    </Card>
  );
}

export default ValidationActionsCard;
